/**
 * When to wake somebody else's device, and with what.
 *
 * A peer-to-peer message only travels while both ends are running. The push
 * the server sends (see server/relay/lib/push.js) is the one thing that can
 * start a phone whose app has been closed, so everything here is about not
 * missing the moments that need one — which the first version did, all the
 * time:
 *
 *   - It woke only members who had not announced themselves for 45 seconds.
 *     A friend who put the phone down half a minute ago still counted as
 *     online, was not woken, and the message waited until they next opened
 *     the app by hand.
 *   - It woke at most once every 20 seconds per chat and simply dropped the
 *     rest. A message sent ten seconds after another one — the usual case —
 *     never woke anybody.
 *   - It woke once. If that push came while the phone could not start the
 *     app, nothing ever tried again.
 *
 * Now: somebody heard from a moment ago gets a quiet wake ("nudge") that a
 * phone acts on and a browser never shows, everybody else a full one; a wake
 * that falls inside the window of the previous one is sent at the end of
 * that window instead of being lost; and a little later, whoever has still
 * not shown up in the chat is woken again.
 *
 * The same machinery runs the other way ("sync"): a device that opens a
 * chat and finds nobody there asks the server to wake the others, because
 * the messages it is missing exist only on their phones.
 */

/* Heard from within this long: probably running, so a quiet wake is enough. */
export const SEEN_FRESH = 25 * 1000

/* At most one wake per person per this, and the last one is never dropped. */
export const COALESCE = 15 * 1000

/* Whoever has not appeared in the chat by then is woken again. */
export const FOLLOW_UPS = [30 * 1000, 3 * 60 * 1000]

/* How long a freshly opened chat is given to find somebody on its own. */
export const PULL_GRACE = 4000
/* And how often one chat, or one person, may be asked for again. */
export const PULL_ROOM_EVERY = 2 * 60 * 1000
export const PULL_ACCOUNT_EVERY = 10 * 60 * 1000

/* What a chat with this many people in it is not woken for on launch. */
const PULL_MAX_MEMBERS = 8
const PULL_MAX_ROOMS = 20

const RANK = { nudge: 1, msg: 2 }

export class Wakes {
  constructor (client, { coalesce = COALESCE, followUps = FOLLOW_UPS, pullGrace = PULL_GRACE } = {}) {
    this.client = client
    this.coalesce = coalesce
    this.followUpDelays = followUps
    this.pullGrace = pullGrace

    this.lastWake = new Map()  // account -> { at, kind }
    this.trailing = new Map()  // account -> { timer, kind }
    this.batch = new Map()     // kind -> Set(account)
    this._batchTimer = null
    this.pulledRooms = new Map()    // roomId -> at
    this.pulledAccounts = new Map() // account -> at
    this._timers = new Set()
    this.closed = false
  }

  close () {
    this.closed = true
    for (const timer of this._timers) clearTimeout(timer)
    this._timers.clear()
    for (const { timer } of this.trailing.values()) clearTimeout(timer)
    this.trailing.clear()
    if (this._batchTimer) clearTimeout(this._batchTimer)
    this._batchTimer = null
  }

  /* ---- after a message of ours ---- */

  async messageSent (room) {
    if (this.closed) return
    const others = await this._others(room)
    if (!others.length) return

    const sentAt = Date.now()
    for (const account of others) this._wake(account, this._kindFor(room, account, sentAt))

    // One schedule per chat, moved along by each new message: a burst of ten
    // messages is checked on once, after the last of them.
    if (room._wakeFollowUps) for (const timer of room._wakeFollowUps) this._clear(timer)
    room._wakeFollowUps = this.followUpDelays.map((delay) => this._later(delay, () => {
      if (room.closed || this.closed) return
      for (const account of others) {
        if (!(lastSeen(room, account) > sentAt)) this._wake(account, 'msg')
      }
    }))
  }

  _kindFor (room, account, now) {
    const seen = lastSeen(room, account)
    return seen && now - seen < SEEN_FRESH ? 'nudge' : 'msg'
  }

  /**
   * One person, one kind. Inside the window of the previous wake this does
   * not send — the server would refuse it anyway — but makes sure one more
   * goes out when the window closes, carrying the stronger of the two kinds.
   */
  _wake (account, kind) {
    const now = Date.now()
    const last = this.lastWake.get(account)
    if (last && now - last.at < this.coalesce) {
      const pending = this.trailing.get(account)
      if (pending) {
        if (RANK[kind] > RANK[pending.kind]) pending.kind = kind
        return
      }
      const entry = { kind, timer: null }
      entry.timer = this._later(this.coalesce - (now - last.at) + 250, () => {
        this.trailing.delete(account)
        this._send(account, entry.kind)
      })
      this.trailing.set(account, entry)
      return
    }
    this._send(account, kind)
  }

  _send (account, kind) {
    if (this.closed) return
    this.lastWake.set(account, { at: Date.now(), kind })
    let set = this.batch.get(kind)
    if (!set) this.batch.set(kind, (set = new Set()))
    set.add(account)
    // Everyone woken in the same moment goes in one request.
    if (!this._batchTimer) {
      this._batchTimer = setTimeout(() => this._flush(), 30)
      if (this._batchTimer.unref) this._batchTimer.unref()
    }
  }

  _flush () {
    this._batchTimer = null
    const batches = [...this.batch.entries()]
    this.batch.clear()
    for (const [kind, accounts] of batches) {
      if (accounts.size) this.client.wake([...accounts], kind)
    }
  }

  /* ---- asking for what we missed ---- */

  /**
   * A chat was opened. If nobody else in it turns up within a few seconds,
   * their devices are asked to wake: whatever was said while this one was
   * away is on their phones and nowhere else.
   */
  pullSoon (room) {
    if (this.closed || !room) return
    this._later(this.pullGrace, () => {
      if (room.closed || this.closed) return
      this.pull([room]).catch(() => {})
    })
  }

  /** The same for the most recent chats, when the app starts or comes back. */
  async pullRecent () {
    if (this.closed) return
    const rooms = []
    for (const record of this.client.listRooms()) {
      if (rooms.length >= PULL_MAX_ROOMS) break
      if (record.placeholder || record.archived || record.deleted) continue
      if ((record.memberCount || 2) > PULL_MAX_MEMBERS) continue
      const room = this.client.rooms.get(record.id)
      if (room && !room.closed) rooms.push(room)
    }
    return this.pull(rooms)
  }

  async pull (rooms) {
    const now = Date.now()
    const ask = new Set()
    for (const room of rooms) {
      if (now - (this.pulledRooms.get(room.id) || 0) < PULL_ROOM_EVERY) continue
      const others = await this._others(room)
      let missing = 0
      for (const account of others) {
        const seen = lastSeen(room, account)
        if (seen && now - seen < SEEN_FRESH) continue
        missing++
        if (now - (this.pulledAccounts.get(account) || 0) < PULL_ACCOUNT_EVERY) continue
        ask.add(account)
      }
      if (missing) this.pulledRooms.set(room.id, now)
    }
    if (!ask.size) return []
    for (const account of ask) this.pulledAccounts.set(account, now)
    this.client.wake([...ask], 'sync')
    return [...ask]
  }

  /* ---- helpers ---- */

  async _others (room) {
    const me = this.client.identity && this.client.identity.id
    const members = await room.view.members().catch(() => [])
    return members.map((m) => m.account).filter((a) => a && a !== me)
  }

  _later (ms, fn) {
    const timer = setTimeout(() => {
      this._timers.delete(timer)
      fn()
    }, ms)
    if (timer.unref) timer.unref()
    this._timers.add(timer)
    return timer
  }

  _clear (timer) {
    clearTimeout(timer)
    this._timers.delete(timer)
  }
}

function lastSeen (room, account) {
  return typeof room.lastSeen === 'function' ? room.lastSeen(account) : 0
}
